import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Play, Sparkles } from "lucide-react";
import { openBookConsultation } from "../../lib/bookConsultation";

const TEAM = [
  {
    src: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=900&auto=format&fit=crop",
    alt: "Team collaboration",
    tint: "#3BA7D1",
    chip: "Pipeline design",
  },
  {
    src: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=900&auto=format&fit=crop",
    alt: "Strategy session",
    tint: "#FFB000",
    chip: "Strategy",
  },
  {
    src: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=900&auto=format&fit=crop",
    alt: "Team portrait",
    tint: "#E96A7F",
    chip: "Editorial",
  },
  {
    src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=900&auto=format&fit=crop",
    alt: "AI workshop",
    tint: "#0FB5A6",
    chip: "AI ops",
  },
];

export default function Hero() {
  const reduce = useReducedMotion();

  const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
  };
  const line = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <section
      data-testid="hero-section"
      className="relative bg-[#FAFAFB] pt-28 md:pt-32 pb-12 md:pb-16 overflow-hidden"
    >
      {/* Soft pastel aurora blobs — more vibrant */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-40 -left-32 w-[720px] h-[720px] rounded-full blur-[90px] opacity-95 float-y-slow"
          style={{
            background:
              "radial-gradient(circle, rgba(255,176,0,0.42), transparent 60%)",
          }}
        />
        <div
          className="absolute -top-20 -right-24 w-[620px] h-[620px] rounded-full blur-[90px] opacity-95 float-y"
          style={{
            background:
              "radial-gradient(circle, rgba(59,167,209,0.38), transparent 60%)",
          }}
        />
        <div
          className="absolute top-[40%] -left-20 w-[560px] h-[560px] rounded-full blur-[100px] opacity-85"
          style={{
            background:
              "radial-gradient(circle, rgba(233,106,127,0.32), transparent 60%)",
          }}
        />
        <div
          className="absolute top-[30%] right-0 w-[540px] h-[540px] rounded-full blur-[100px] opacity-85 float-y"
          style={{
            background:
              "radial-gradient(circle, rgba(15,181,166,0.32), transparent 60%)",
          }}
        />
        <div
          className="absolute bottom-0 left-1/3 w-[640px] h-[320px] rounded-full blur-[100px] opacity-80"
          style={{
            background:
              "radial-gradient(circle, rgba(107,119,232,0.28), transparent 60%)",
          }}
        />
      </div>

      {/* Large decorative concentric rings */}
      <svg
        className="absolute inset-0 m-auto pointer-events-none opacity-[0.22] w-[1400px] h-[1400px] max-w-[140vw] max-h-[140vw]"
        viewBox="0 0 800 800"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <circle cx="400" cy="400" r="160" stroke="#1D1D1F" strokeOpacity="0.4" strokeWidth="1" />
        <circle cx="400" cy="400" r="230" stroke="#1D1D1F" strokeOpacity="0.28" strokeWidth="1" />
        <circle cx="400" cy="400" r="310" stroke="#1D1D1F" strokeOpacity="0.18" strokeWidth="1" />
        <circle cx="400" cy="400" r="390" stroke="#1D1D1F" strokeOpacity="0.12" strokeWidth="1" />
      </svg>

      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.3]"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(29,29,31,0.08) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          maskImage:
            "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)",
        }}
      />

      {/* Floating colored orb/shape accents */}
      <motion.span
        aria-hidden
        initial={reduce ? false : { opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.4 }}
        className="hidden md:block absolute top-[22%] left-[6%] h-14 w-14 rounded-2xl rotate-12 float-y-slow"
        style={{
          background:
            "linear-gradient(135deg, #FFB000 0%, #FF8A3C 100%)",
          boxShadow: "0 20px 40px -12px rgba(255,138,60,0.45)",
        }}
      />
      <motion.span
        aria-hidden
        initial={reduce ? false : { opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.55 }}
        className="hidden md:block absolute top-[18%] right-[8%] h-16 w-16 rounded-full float-y"
        style={{
          background: "linear-gradient(135deg, #3BA7D1 0%, #6B77E8 100%)",
          boxShadow: "0 20px 40px -12px rgba(107,119,232,0.45)",
        }}
      />
      <motion.span
        aria-hidden
        initial={reduce ? false : { opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.7 }}
        className="hidden md:block absolute bottom-[12%] left-[10%] h-10 w-10 rounded-full float-y-slow"
        style={{
          background: "linear-gradient(135deg, #E96A7F 0%, #C44B8A 100%)",
          boxShadow: "0 16px 32px -8px rgba(233,106,127,0.5)",
        }}
      />
      <motion.span
        aria-hidden
        initial={reduce ? false : { opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.85 }}
        className="hidden md:block absolute bottom-[18%] right-[7%] h-12 w-12 rounded-xl -rotate-6 float-y"
        style={{
          background: "linear-gradient(135deg, #0FB5A6 0%, #B7D455 100%)",
          boxShadow: "0 20px 40px -12px rgba(15,181,166,0.45)",
        }}
      />

      {/* Floating labeled chips */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5 }}
        className="hidden lg:block absolute left-[5%] top-[42%] z-10"
      >
        <FloatChip label="+98% pipeline" color="#FFB000" />
      </motion.div>
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.65 }}
        className="hidden lg:block absolute right-[5%] top-[38%] z-10"
      >
        <FloatChip label="Fortune 1000" color="#3BA7D1" />
      </motion.div>

      <div className="relative container-xl text-center">
        {/* Overline chip */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-2 bg-white border border-black/8 rounded-full pl-1.5 pr-4 py-1.5 mb-8 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.25)]"
        >
          <span className="inline-flex items-center gap-1.5 bg-[#1D1D1F] text-white rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-medium">
            <Sparkles size={11} color="#FFB000" strokeWidth={2.2} />
            New
          </span>
          <span className="text-[12px] tracking-tight text-[#1D1D1F]">
            Full-funnel, AI-led, enterprise B2B
          </span>
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={reduce ? false : "hidden"}
          animate="show"
          variants={stagger}
          className="hero-headline font-display text-[#1D1D1F] font-semibold leading-[0.9] tracking-tighter mx-auto max-w-[1300px]"
          style={{
            fontSize: "clamp(42px, 7vw, 112px)",
            letterSpacing: "-0.05em",
          }}
        >
          <motion.span variants={line} className="block">
            Full-Funnel B2B Marketing,
          </motion.span>
          <motion.span
            variants={line}
            className="block font-light text-[#86868B]"
          >
            Powered by{" "}
            <span className="font-serif italic font-normal gradient-text-vivid">
              AI &amp; Automation.
            </span>
          </motion.span>
        </motion.h1>

        <motion.p
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 text-[#515154] text-base md:text-lg max-w-2xl mx-auto leading-relaxed font-light"
        >
          Top-funnel discovery. Personalised account penetration. Mid-funnel
          engagement. A steady pipeline of qualified leads — engineered with AI.
        </motion.p>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-3"
        >
          <button
            type="button"
            onClick={() => openBookConsultation("hero_primary")}
            data-testid="hero-cta-button"
            className="btn-pill btn-primary-amber text-[15px] px-6 py-3.5"
          >
            Book a consultation
            <ArrowUpRight size={16} strokeWidth={2.2} />
          </button>
          <a
            href="#how"
            data-testid="hero-secondary-cta"
            className="btn-pill btn-ghost-light text-[15px] px-6 py-3.5"
          >
            <Play size={14} strokeWidth={2.2} />
            How it works
          </a>
        </motion.div>

        {/* Centered human imagery — 4 tile stagger */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 md:mt-14 relative"
        >
          {/* Colorful halo behind imagery */}
          <div className="absolute inset-x-0 -top-4 h-[95%] max-w-6xl mx-auto pointer-events-none">
            <div className="absolute inset-0 rounded-[48px] bg-gradient-to-r from-[#FFB000]/35 via-[#E96A7F]/30 to-[#3BA7D1]/35 blur-2xl opacity-90" />
          </div>

          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-6xl mx-auto">
            {TEAM.map((t, i) => (
              <motion.div
                key={i}
                initial={reduce ? false : { opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  delay: 1.2 + i * 0.08,
                  duration: 0.9,
                  ease: [0.22, 1, 0.36, 1],
                }}
                data-testid={`hero-people-${i}`}
                className={`group card-lift relative aspect-[4/5] rounded-[22px] md:rounded-[28px] overflow-hidden border-[3px] border-white shadow-[0_30px_60px_-28px_rgba(0,0,0,0.35)] ${
                  i === 1 || i === 2 ? "md:-translate-y-5" : ""
                }`}
              >
                <img
                  src={t.src}
                  alt={t.alt}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(180deg, transparent 45%, ${t.tint}30 100%)`,
                  }}
                />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span
                    className="text-[9px] md:text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full backdrop-blur-md border"
                    style={{
                      background: "rgba(255,255,255,0.82)",
                      borderColor: "rgba(0,0,0,0.08)",
                      color: "#1D1D1F",
                    }}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                      style={{ background: t.tint }}
                    />
                    {t.chip}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Stats strip centered */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.8 }}
          className="mt-14 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-5xl mx-auto border-t border-black/8 pt-10"
        >
          {[
            { k: "Fortune 1000", v: "Clients served", c: "#FFB000" },
            { k: "12+ yrs", v: "B2B specialisation", c: "#3BA7D1" },
            { k: "8 countries", v: "Campaign footprint", c: "#E96A7F" },
            { k: "AI-native", v: "Full-funnel ops", c: "#0FB5A6" },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`hero-strip-stat-${i}`}
              className="relative text-center md:text-left md:pl-4"
            >
              <span
                className="hidden md:block absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
                style={{ background: s.c }}
              />
              <div className="font-display text-xl md:text-2xl lg:text-3xl tracking-tight text-[#1D1D1F] font-medium">
                {s.k}
              </div>
              <div className="text-[#86868B] text-xs md:text-sm uppercase tracking-[0.18em] mt-1">
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function FloatChip({ label, color }) {
  return (
    <div
      className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-md border rounded-full px-4 py-2 text-[12px] font-medium text-[#1D1D1F] shadow-[0_20px_40px_-16px_rgba(0,0,0,0.25)]"
      style={{ borderColor: `${color}55` }}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 10px ${color}` }}
      />
      {label}
    </div>
  );
}
